import { useEffect, useState } from 'react';

export type UseScriptStatus = 'idle' | 'loading' | 'ready' | 'error' | 'timeout';
export interface UseScriptOptions {
	removeOnUnmount?: boolean;
	timeoutMs?: number;
	crossOrigin?: HTMLScriptElement['crossOrigin'];
	referrerPolicy?: HTMLScriptElement['referrerPolicy'];
	integrity?: string;
}

const cachedScriptStatuses: Record<string, UseScriptStatus | undefined> = {};
const scriptLoadPromises: Record<string, Promise<UseScriptStatus> | undefined> = {};

function getScriptNode(src: string) {
	const node: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
	const status = node?.getAttribute('data-status') as UseScriptStatus | undefined;
	return { node, status };
}

function isRecaptchaSrc(src: string) {
	return src.includes('google.com/recaptcha/api.js') || src.includes('recaptcha/api.js');
}

function getOrCreateScriptLoadPromise(
	src: string,
	options?: Pick<UseScriptOptions, 'timeoutMs' | 'crossOrigin' | 'referrerPolicy' | 'integrity'>
): Promise<UseScriptStatus> {
	const existingPromise = scriptLoadPromises[src];
	if (existingPromise) return existingPromise;

	const script = getScriptNode(src);
	let scriptNode = script.node;
	if (!scriptNode) {
		scriptNode = document.createElement('script');
		scriptNode.src = src;
		scriptNode.async = true;
		if (options?.crossOrigin !== undefined) scriptNode.crossOrigin = options.crossOrigin;
		if (options?.referrerPolicy) scriptNode.referrerPolicy = options.referrerPolicy;
		if (options?.integrity) scriptNode.integrity = options.integrity;
		scriptNode.setAttribute('data-status', 'loading');
	}

	const promise = new Promise<UseScriptStatus>((resolve) => {
		let settled = false;
		let timeoutId: number | undefined;

		const cleanup = () => {
			if (!scriptNode) return;
			scriptNode.removeEventListener('load', onLoad);
			scriptNode.removeEventListener('error', onError);
			if (timeoutId !== undefined) window.clearTimeout(timeoutId);
		};

		const finalize = (newStatus: UseScriptStatus) => {
			if (settled) return;
			settled = true;
			cleanup();
			scriptNode?.setAttribute('data-status', newStatus);
			cachedScriptStatuses[src] = newStatus;
			resolve(newStatus);
		};

		const onLoad = () => {
			finalize('ready');
		};
		const onError = () => {
			finalize('error');
		};

		const scriptElement = scriptNode as (HTMLScriptElement & { complete?: boolean; readyState?: string }) | null;
		const alreadyReady =
			scriptNode?.getAttribute('data-status') === 'ready' ||
			scriptElement?.readyState === 'complete' ||
			scriptElement?.complete ||
			(isRecaptchaSrc(src) && Boolean(window.grecaptcha));
		if (alreadyReady) {
			finalize('ready');
			return;
		}

		if (scriptNode?.getAttribute('data-status') === 'error') {
			finalize('error');
			return;
		}

		if (options?.timeoutMs && options.timeoutMs > 0) {
			timeoutId = window.setTimeout(() => {
				finalize('timeout');
			}, options.timeoutMs);
		}

		scriptNode?.addEventListener('load', onLoad);
		scriptNode?.addEventListener('error', onError);

		if (!script.node) {
			document.body.appendChild(scriptNode);
		}
	});

	scriptLoadPromises[src] = promise;
	promise.finally(() => {
		scriptLoadPromises[src] = undefined;
	});
	return promise;
}

export function useScript(src: string | null, options?: UseScriptOptions): UseScriptStatus {
	const [status, setStatus] = useState<UseScriptStatus>(() => {
		if (!src) return 'idle';
		if (typeof window === 'undefined') return 'loading'; // SSR
		return cachedScriptStatuses[src] ?? 'loading';
	});

	useEffect(() => {
		if (!src) {
			return;
		}

		let active = true;
		const cachedScriptStatus = cachedScriptStatuses[src];
		if (cachedScriptStatus === 'ready' || cachedScriptStatus === 'error' || cachedScriptStatus === 'timeout') {
			setStatus(cachedScriptStatus);
			return;
		}

		const script = getScriptNode(src);
		const scriptElement = script.node as (HTMLScriptElement & { complete?: boolean; readyState?: string }) | null;
		const recaptchaReady = isRecaptchaSrc(src) && Boolean(window.grecaptcha);
		const existingStatus =
			script.status ??
			(scriptElement?.readyState === 'complete' || scriptElement?.complete ? 'ready' : undefined) ??
			(recaptchaReady ? 'ready' : undefined) ??
			cachedScriptStatus ??
			'loading';

		setStatus(existingStatus);
		cachedScriptStatuses[src] = existingStatus;

		if (existingStatus === 'ready' || existingStatus === 'error' || existingStatus === 'timeout') {
			return;
		}

		getOrCreateScriptLoadPromise(src, {
			timeoutMs: options?.timeoutMs,
			crossOrigin: options?.crossOrigin,
			referrerPolicy: options?.referrerPolicy,
			integrity: options?.integrity,
		}).then((newStatus) => {
			if (active) {
				setStatus(newStatus);
			}
		});

		return () => {
			active = false;
			const scriptNode = getScriptNode(src).node;
			if (scriptNode && options?.removeOnUnmount) {
				scriptNode.remove();
			}
		};
	}, [
		src,
		options?.removeOnUnmount,
		options?.timeoutMs,
		options?.crossOrigin,
		options?.referrerPolicy,
		options?.integrity,
	]);

	return status;
}
